import { useCallback, useMemo } from 'react';

import type { DxfLayer } from '@frontend/api';

import type { DrawingModel, DxfViewerSession } from '../session/types';
import { useReadableStore } from '../session/useSessionState';

export type DxfLayerPanelState = {
  layers: readonly DxfLayer[];
  hiddenLayers: ReadonlySet<number>;
  counts: ReadonlyMap<number, number>;
  /**
   * Layers whose every selectable entity is selected.
   *
   * All or nothing: a partially-caught layer is absent, so `has` answers "is this
   * layer selected" outright with no threshold left to the caller.
   */
  selectedLayers: ReadonlySet<number>;
  selectableLayers: ReadonlySet<number>;
  indeterminateLayers: ReadonlySet<number>;
  onToggleLayer: (layerIndex: number, nextVisible: boolean) => void;
  onSelectLayer: (layerIndex: number, isAdditive: boolean) => void;
  onFrameLayer: (layerIndex: number) => void;
  onHoverLayer: (layerIndex: number | null) => void;
  isLayerColoringOn: boolean;
  onToggleLayerColoring: () => void;
};

/**
 * Connects `DxfLayerPanel` to the session. Call only under a ready drawing.
 */
export const useDxfLayerPanel = (
  session: DxfViewerSession,
  model: DrawingModel,
): DxfLayerPanelState => {
  const hiddenLayers = useReadableStore(session.hiddenLayers);
  const selection = useReadableStore(session.selection);
  const isLayerColoringOn = useReadableStore(
    session.overlays,
    (current) => current.layerColoring,
  );
  const isPmiVisible = useReadableStore(
    session.overlays,
    (current) => current.pmi,
  );

  // A hidden layer that still carries visible PMI is neither on nor off, and the
  // checkbox has to say so rather than claiming the layer is fully hidden.
  const indeterminateLayers = useMemo(() => {
    if (!isPmiVisible) {
      return new Set<number>();
    }
    const partial = new Set<number>();
    hiddenLayers.forEach((layerIndex) => {
      if (model.layersWithPmi.has(layerIndex)) {
        partial.add(layerIndex);
      }
    });
    return partial;
  }, [isPmiVisible, model, hiddenLayers]);

  /**
   * The source entities on each layer that can actually be selected.
   *
   * Walked from the chunks rather than taken from `entityCountsByLayer`, because
   * that count is over *every* entity on a layer — text, hatch fills and bare
   * POINTs included — and none of those are stitched, so none of them can ever be
   * picked. Using it as the denominator below would mean a layer carrying a single
   * label could never read as fully selected however much of it the user chose.
   */
  const selectableEntityKeysByLayer = useMemo(() => {
    const byLayer = new Map<number, Set<string>>();
    model.loops.forEach((loop) =>
      loop.chunks.forEach((chunk) => {
        const keys = byLayer.get(chunk.layer);
        if (keys) {
          keys.add(chunk.entityId);
          return;
        }
        byLayer.set(chunk.layer, new Set([chunk.entityId]));
      }),
    );
    return byLayer;
  }, [model]);

  // A layer with no stitched geometry has nothing to select, so its name stays
  // inert rather than looking clickable and doing nothing.
  const selectableLayers = useMemo(
    () => new Set(selectableEntityKeysByLayer.keys()),
    [selectableEntityKeysByLayer],
  );

  /**
   * Layers whose every selectable entity is selected.
   *
   * All or nothing, because that is what the panel needs of it: a row is either
   * selected or it is not, and a partially-caught layer is not. So a layer only
   * appears here once the selection covers all of it.
   *
   * Compared over distinct source entities, since one selected loop can be
   * stitched from many entities and one entity can contribute chunks to several
   * loops — counting refs would match neither the layer's contents nor itself.
   * Because selected keys are always read back off chunks, they are a subset of
   * the layer's selectable keys by construction, which is what makes comparing
   * sizes sufficient rather than needing a per-key containment check.
   *
   * A loop stitched across two layers contributes to both, and completes neither
   * on its own.
   *
   * Hidden layers are not excluded. A layer can still hold selection while hidden
   * — `pruneHiddenFromSelection` only drops a loop once *every* chunk of it is
   * hidden — and reporting that is more use than concealing it, the same reason a
   * hidden layer carrying PMI reads as indeterminate rather than off.
   */
  const selectedLayers = useMemo(() => {
    const fullySelected = new Set<number>();
    if (selection.size === 0) {
      return fullySelected;
    }

    const selectedKeysByLayer = new Map<number, Set<string>>();
    selection.forEach((ref) => {
      const loop = model.loopsById.get(ref.loopId);
      if (!loop) {
        return;
      }
      const chunks =
        ref.kind === 'edge' ? [loop.chunks[ref.chunkIndex]] : loop.chunks;

      chunks.forEach((chunk) => {
        if (!chunk) {
          return;
        }
        const keys = selectedKeysByLayer.get(chunk.layer);
        if (keys) {
          keys.add(chunk.entityId);
          return;
        }
        selectedKeysByLayer.set(chunk.layer, new Set([chunk.entityId]));
      });
    });

    selectedKeysByLayer.forEach((selected, layerIndex) => {
      if (selected.size === selectableEntityKeysByLayer.get(layerIndex)?.size) {
        fullySelected.add(layerIndex);
      }
    });
    return fullySelected;
  }, [selection, model, selectableEntityKeysByLayer]);

  const onToggleLayer = useCallback(
    (layerIndex: number, nextVisible: boolean) => {
      session.layers.setVisible(layerIndex, nextVisible);
    },
    [session],
  );

  const onSelectLayer = useCallback(
    (layerIndex: number, isAdditive: boolean) => {
      session.layers.select(layerIndex, isAdditive ? 'add' : 'replace');
    },
    [session],
  );

  const onFrameLayer = useCallback(
    (layerIndex: number) => session.layers.frame(layerIndex),
    [session],
  );

  const onHoverLayer = useCallback(
    (layerIndex: number | null) => session.layers.setHovered(layerIndex),
    [session],
  );

  const onToggleLayerColoring = useCallback(
    () => session.overlays.toggle('layerColoring'),
    [session],
  );

  return {
    layers: model.scene.layers,
    hiddenLayers,
    counts: model.entityCountsByLayer,
    selectedLayers,
    selectableLayers,
    indeterminateLayers,
    onToggleLayer,
    onSelectLayer,
    onFrameLayer,
    onHoverLayer,
    isLayerColoringOn,
    onToggleLayerColoring,
  };
};
